/**
 * EnvironmentalMeasurement Types for MEV-Med Frontend
 * Type definitions for environmental measurements (#351)
 *
 * @version 2026.1.0
 * @description Tipos TypeScript das medições ambientais. A medição continua
 *              sendo RiskApplicationMeasurements — empresa, setor e função vêm
 *              da aplicação de risco (scope + scopeId).
 */

import type {
  HazardousMeasurementFields,
  RiskApplicationScope,
  UnhealthyMeasurementFields,
} from './riskApplication';

/**
 * Os seis anexos da NR-16, lista fixa até a #417 carregar o catálogo de agentes.
 */
export const NR16_ANNEXES = [
  'Anexo 1 — Explosivos',
  'Anexo 2 — Inflamáveis',
  'Anexo 3 — Roubos ou outras espécies de violência física',
  'Anexo 4 — Energia elétrica',
  'Anexo 5 — Motocicleta',
  'Radiações ionizantes ou substâncias radioativas',
] as const;

/**
 * #417 — a natureza da avaliação é escolha do técnico na medição, e não vem do
 * catálogo: qualitativa é o que a empresa registra até o laboratório entregar o
 * resultado. Campo próprio, separado de `technique`, que guarda o método do laudo.
 */
export const MEASUREMENT_ASSESSMENT_TYPES = ['QUALITATIVE', 'QUANTITATIVE'] as const;
export type MeasurementAssessmentType = typeof MEASUREMENT_ASSESSMENT_TYPES[number];

export const MEASUREMENT_ASSESSMENT_TYPE_LABELS: Record<MeasurementAssessmentType, string> = {
  QUALITATIVE: 'Qualitativa',
  QUANTITATIVE: 'Quantitativa',
};

/**
 * EnvironmentalMeasurement entity interface
 * Espelha EnvironmentalMeasurementResponse do backend
 */
export interface EnvironmentalMeasurement
  extends Required<HazardousMeasurementFields>, Required<UnhealthyMeasurementFields> {
  id: number;
  riskApplicationId: number;
  measurementEquipmentId: number | null;
  measurementEquipmentName: string | null;
  /** DECIMAL do Sequelize volta como string — o backend normaliza com Number().
   *  Nulo na periculosidade: a NR-16 enquadra a atividade em vez de medir (#424) */
  value: number | null;
  unit: string | null;
  measuredAt: string;
  observations: string | null;
  technique: string | null;
  exposureTime: string | null;
  responsibleUserId: number | null;
  responsibleUserName: string | null;
  /** Informado pelo técnico, nunca calculado pela tela (D2 do plano) */
  exceedsLimit: boolean;
  riskId: number | null;
  /** Natureza do risco, vinda do catálogo por junção: decide o cartão e o chip de enquadramento */
  riskIsHazardous: boolean;
  /** Risco com agente insalubre (NR-15): decide o cartão de enquadramento de insalubridade */
  riskIsUnhealthy: boolean;
  /** Grau cadastrado no risco: sugestão de valor inicial, nunca imposição (#357) */
  riskUnhealthyDegree: string | null;
  riskFactorName: string | null;
  /** Texto livre do risco, exibido como apoio à decisão do técnico */
  toleranceLimit: string | null;
  measurementUnit: string | null;
  /** #417 — o limite escolhido nesta medição, e de qual norma ele veio. Quando
   *  preenchido, é ele que a tela mostra; o do risco só aparece na ausência dele. */
  appliedToleranceLimit: string | null;
  appliedToleranceLimitSource: string | null;
  /** Qualitativa ou Quantitativa: o estágio da empresa, não a técnica do laudo */
  assessmentType: MeasurementAssessmentType | null;
  scope: RiskApplicationScope | null;
  scopeId: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Filters accepted by both the list and the export endpoints
 */
export interface EnvironmentalMeasurementFilters {
  search?: string;
  companyId?: number;
  departmentId?: number;
  occupationId?: number;
  riskId?: number;
  measurementEquipmentId?: number;
  startDate?: string;
  endDate?: string;
  exceedsLimit?: boolean;
  hazardousFraming?: boolean;
  unhealthyFraming?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Response for list endpoint with pagination
 */
export interface EnvironmentalMeasurementListResponse {
  measurements: EnvironmentalMeasurement[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Form data for creating/editing a measurement
 */
/**
 * Os campos de periculosidade no formulário são texto: o campo vazio é a ausência
 * do dado, e é o serviço que converte para número ou nulo antes de enviar.
 */
export interface HazardousMeasurementFormFields {
  storedQuantity: string;
  storedQuantityUnit: string;
  transportedQuantity: string;
  transportedQuantityUnit: string;
  riskArea: string;
  storageType: string;
  hazardousFraming: boolean;
  hazardousAnnex: string;
}

/**
 * O grau no formulário é texto: o campo vazio é a ausência do dado, e é o serviço que
 * converte para nulo antes de enviar.
 */
export interface UnhealthyMeasurementFormFields {
  unhealthyFraming: boolean;
  unhealthyDegreeApplied: string;
}

export interface EnvironmentalMeasurementFormData
  extends HazardousMeasurementFormFields, UnhealthyMeasurementFormFields {
  riskApplicationId: number | null;
  measurementEquipmentId: number | null;
  value: string;
  unit: string;
  measuredAt: string;
  technique: string;
  exposureTime: string;
  responsibleUserId: number | null;
  observations: string;
  exceedsLimit: boolean;
  appliedToleranceLimit: string;
  appliedToleranceLimitSource: string;
  assessmentType: MeasurementAssessmentType | '';
}

/**
 * API payload for creating/updating a measurement
 */
export interface EnvironmentalMeasurementApiPayload
  extends HazardousMeasurementFields, UnhealthyMeasurementFields {
  riskApplicationId?: number;
  measurementEquipmentId?: number | null;
  value: number | null;
  unit?: string | null;
  measuredAt: string;
  technique?: string | null;
  exposureTime?: string | null;
  responsibleUserId?: number | null;
  observations?: string | null;
  exceedsLimit: boolean;
  appliedToleranceLimit?: string | null;
  appliedToleranceLimitSource?: string | null;
  assessmentType?: MeasurementAssessmentType | null;
}

/**
 * Contexto do risco medido, exibido no formulário como apoio à decisão
 */
export interface MeasuredRiskContext {
  riskFactorName: string | null;
  toleranceLimit: string | null;
  measurementUnit: string | null;
  /** Risco de periculosidade (NR-16): decide o cartão de enquadramento no lugar do excedente */
  isHazardous: boolean;
  /** Risco de insalubridade (NR-15): acrescenta o cartão de enquadramento da insalubridade */
  isUnhealthy: boolean;
  /** Grau do catálogo, sugerido ao marcar o enquadramento — o profissional confirma ou troca */
  unhealthyDegree: string | null;
  /** Ficha do catálogo ligada ao risco, de onde saem as opções de limite (#417) */
  hazardousAgentId: number | null;
}

export interface EnvironmentalMeasurementFormProps {
  initialData?: EnvironmentalMeasurement;
  onSubmit: (data: EnvironmentalMeasurementFormData) => Promise<void> | void;
  onCancel: () => void;
  loading?: boolean;
}
